import {
  WithAnalyticsEventsProps,
  useAnalyticsEvents,
  withAnalyticsEvents
} from '@atlaskit/analytics-next';
import { AsyncResult } from '@atlassiansox/bxp-react-core';
import {
  ButtonProps,
  DropdownSelector,
  TextInput,
  breakpoints
} from '@atlassiansox/wpl-utils';
import {
  MouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { ANALYTICS_EVENTS } from '../../constants';
import { usePricingData } from '../../hooks';
import {
  PlanName,
  ProductFamilyName,
  ProductName,
  ProductPlan,
  UnitName
} from '../../models';
import { useModuleProps } from '../../module-props';
import { useGlobalStore } from '../../store';
import {
  GetContactMessage,
  GetEnterpriseAnnualOnlyMessage,
  GetEnterpriseMessage,
  GetInputLabel,
  GetMaxUsersMessage,
  GetMinUsersMessage,
  GetTierMessage,
  GetUnitPriceMessage,
  GetUserTypeLabel
} from './messages';
import {
  monthlyPriceStyle,
  pricingContainer,
  removeButtonStyle,
  userPriceStyle
} from './pricing.styles';
import {
  maxInputErrorStyle,
  productContainer,
  productIconStyle,
  productInputContainer,
  productLimitErrorStyle,
  productLimitStyle,
  productNameStyle,
  productWrapperContainer,
  userInputContainerStyles
} from './product-card.styles';
import {
  productLogoNameContainer,
  productTreeContainer
} from './product-family-container.styles';

interface ProductCardProps {
  name: ProductName;
  icon?: ReactElement;
  plans: ProductPlan[];
  planOptions: { label: string; value: string }[];

  productFamilyName: ProductFamilyName;
  unitName: UnitName;
  noAnnualPlan?: boolean;
}
const RemoveButtonBase = ({
  createAnalyticsEvent,
  onClick,
  text,
  productName
}: WithAnalyticsEventsProps & ButtonProps & { productName: ProductName }) => {
  const handleClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      const analyticsEvent = createAnalyticsEvent?.({
        action: 'click',
        component: 'button',
        eventName: ANALYTICS_EVENTS.removeProduct,
        removedProduct: productName
      });

      analyticsEvent?.fire();

      if (onClick) {
        onClick(e);
      }
    },
    [onClick, createAnalyticsEvent]
  );

  return (
    <button css={removeButtonStyle} onClick={handleClick}>
      {text}
    </button>
  );
};

const RemoveButton = withAnalyticsEvents()(RemoveButtonBase);

const ProductCard = ({
  name,
  icon,
  plans,
  planOptions,
  productFamilyName,
  unitName,
  noAnnualPlan
}: ProductCardProps) => {
  const [{ scrollToAddedProduct }] = useGlobalStore();
  const [
    {
      selectedProductNames: totalSelectedProductNames,
      selectedBillingCycle,
      selectedProductsWithDetails
    },
    {
      toggleProductSelection,
      incrementTotal,
      setFreePlanProduct,
      removeFreePlanProduct,
      setUnitCountByProduct,
      selectStep,
      updateSelectedProductsWithDetails,
      restart
    }
  ] = useGlobalStore();

  useEffect(() => {
    const productsWithDetails = new Map();
    [...totalSelectedProductNames].forEach((name) => {
      if (selectedProductsWithDetails.has(name)) {
        productsWithDetails.set(name, selectedProductsWithDetails.get(name));
      } else {
        productsWithDetails.set(name, {
          name,
          planName: 'None' as PlanName,
          selectedUserCount: 0,
          costPerUserType: '',
          monthlyCost: '',
          userType: '',
          userTypeTier: ''
        });
      }
    });

    updateSelectedProductsWithDetails(productsWithDetails);
  }, [totalSelectedProductNames.size]);

  // scroll window to newly added product if on mobile
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollToAddedProduct) {
      return;
    }

    const htmlRoot = document.querySelector('html');
    if (
      htmlRoot &&
      containerRef.current &&
      htmlRoot.clientWidth < breakpoints.md
    ) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, []);

  const localeISOCode = document.documentElement.lang || 'en-US';
  const { createAnalyticsEvent } = useAnalyticsEvents();

  const [selectedPlanName, setSelectedPlanName] = useState<PlanName>('None');
  const [selectedPlanUnitLimits, setSelectedPlanUnitLimits] = useState<
    ProductPlan['unitLimits']
  >([0, 1]);

  const [ccpOfferingSKU, setCCPOfferingSKU] = useState('');
  const [hamsProductKey, setHamsProductKey] = useState('');

  const [numUnits, setNumUnits] = useState(0);

  const [currentTotalPrice, setCurrentTotalPrice] = useState(0);
  const [formattedTotal, setFormattedTotal] = useState('$0');
  const [perUnit, setPerUnit] = useState('$0');

  const MIN_UNITS: number = selectedPlanUnitLimits[0];
  const MAX_UNITS: number = selectedPlanUnitLimits[1];
  const MIN_ENTERPRISE_UNITS: number = selectedPlanUnitLimits[0] || 801;

  const fetchPriceCalc = usePricingData(
    ccpOfferingSKU,
    hamsProductKey,
    numUnits,
    selectedPlanName,
    selectedPlanUnitLimits,
    noAnnualPlan
  );

  const {
    planLabel,
    productLimitErrorMessage1,
    productLimitErrorMessage2,
    monthlyBillingCycleLabel,
    annuallyBillingCycleLabel,
    removeLabel,
    freeLabel
  } = useModuleProps();

  let unitLimitLower = '';
  let unitLimitUpper = '';
  if (AsyncResult.isComplete(fetchPriceCalc)) {
    const priceCalc = fetchPriceCalc.resolve()[0];

    if (priceCalc) {
      const {
        totalPrice,
        formattedTotalPrice,
        formattedAvgPerUnitPrice,
        formattedBreakdown
      } = priceCalc;

      const fmtBreakdownTier =
        formattedBreakdown[formattedBreakdown.length - 1];

      if (!fmtBreakdownTier) {
        throw new Error('No breakdown provided for user tier.');
      }

      const { formattedUnitLimitLower, formattedUnitLimitUpper } =
        fmtBreakdownTier;

      unitLimitLower = formattedUnitLimitLower;
      unitLimitUpper = formattedUnitLimitUpper;

      if (totalPrice !== currentTotalPrice) {
        incrementTotal(productFamilyName, totalPrice - currentTotalPrice);
        setCurrentTotalPrice(totalPrice);
        setFormattedTotal(formattedTotalPrice);
        setPerUnit(formattedAvgPerUnitPrice);

        const currentProducts = new Map(selectedProductsWithDetails);
        const product = currentProducts.get(name);
        if (product) {
          const changedProduct = {
            ...product,
            costPerUserType:
              product?.planName === 'Free' ? 'Free' : formattedAvgPerUnitPrice,
            monthlyCost:
              product?.planName === 'Free' ? 'Free' : formattedTotalPrice,
            userType: GetUserTypeLabel(unitName),
            userTypeTier: `${unitLimitLower} - ${unitLimitUpper}`
          };
          currentProducts.set(name, changedProduct);
        }
        updateSelectedProductsWithDetails(currentProducts);
      }
    }
  } else if (AsyncResult.isError(fetchPriceCalc)) {
    throw fetchPriceCalc.error;
  }

  return (
    <div css={productTreeContainer} ref={containerRef}>
      <div css={productWrapperContainer}>
        <div css={productContainer}>
          <div css={productLogoNameContainer}>
            <div css={productIconStyle}>{icon}</div>
            <div css={productNameStyle}>{name}</div>
          </div>
          <div css={productInputContainer}>
            <div style={{ position: 'relative', top: '-1px' }}>
              <DropdownSelector
                options={planOptions.filter(
                  (plan) => plan.value !== selectedPlanName
                )}
                name="plan-selector"
                id="plan-selector"
                label={planLabel}
                className="plan-selector"
                aria-label="Plan Selector"
                onChangeHandler={(dropdownSelectedPlanName) => {
                  const selectedPlan = plans.find(
                    (plan) => plan.name === dropdownSelectedPlanName
                  );
                  if (!selectedPlan) {
                    throw new Error(
                      `Plan ${selectedPlanName} not found for ${name}`
                    );
                  }

                  setSelectedPlanName(selectedPlan.name);

                  const currentProducts = new Map(selectedProductsWithDetails);
                  const product = currentProducts.get(name);
                  if (product) {
                    const changedProduct = {
                      ...product,
                      planName: selectedPlan.name
                    };
                    currentProducts.set(name, changedProduct);
                  }
                  updateSelectedProductsWithDetails(currentProducts);

                  setSelectedPlanUnitLimits(selectedPlan.unitLimits);

                  setCCPOfferingSKU(selectedPlan.ccpOfferingSKU);
                  setHamsProductKey(selectedPlan.hamsProductKey);

                  if (
                    selectedPlan.name == 'Free' &&
                    numUnits > 0 &&
                    numUnits <= MAX_UNITS
                  ) {
                    setFreePlanProduct(name);
                  } else {
                    removeFreePlanProduct(name);
                  }

                  const analyticsEvent = createAnalyticsEvent?.({
                    action: 'click',
                    component: 'component',
                    trackEventName: ANALYTICS_EVENTS.changeProductEdition,
                    productEdition: selectedPlan.name,
                    userCount: numUnits,
                    productName: name
                  });

                  analyticsEvent?.fire();
                }}
              />
              {selectedBillingCycle === 'MONTHLY' &&
                selectedPlanName === 'Enterprise' && (
                  <div css={productLimitErrorStyle}>
                    {GetEnterpriseAnnualOnlyMessage(selectedPlanName)}
                  </div>
                )}
              {selectedBillingCycle === 'ANNUAL' &&
                selectedPlanName === 'Free' && (
                  <div css={productLimitErrorStyle}>
                    {selectedPlanName} {productLimitErrorMessage1}
                  </div>
                )}
              {selectedBillingCycle === 'ANNUAL' && noAnnualPlan && (
                <div css={productLimitErrorStyle}>
                  {productLimitErrorMessage2}
                </div>
              )}
            </div>

            <div
              css={[
                numUnits > MAX_UNITS && maxInputErrorStyle,
                userInputContainerStyles
              ]}
            >
              <TextInput
                id="user-count-input"
                className="border-error text-input-container"
                name={name}
                placeholder=""
                label={GetInputLabel(unitName)}
                aria-label="User count input"
                autocomplete="off"
                value={
                  numUnits === 0
                    ? ''
                    : Intl.NumberFormat(localeISOCode).format(numUnits)
                }
                onChangeHandler={(selectedNumUnits) => {
                  const digitsOnly = (selectedNumUnits as string).replace(
                    /\D/g,
                    ''
                  );

                  if (!isNaN(Number(digitsOnly))) {
                    setNumUnits(Number(digitsOnly));
                    setUnitCountByProduct(name, Number(digitsOnly));

                    const currentProducts = new Map(
                      selectedProductsWithDetails
                    );
                    const product = currentProducts.get(name);
                    if (product) {
                      const changedProduct = {
                        ...product,
                        selectedUserCount: Number(digitsOnly)
                      };
                      currentProducts.set(name, changedProduct);
                    }
                    updateSelectedProductsWithDetails(currentProducts);

                    if (
                      selectedPlanName == 'Free' &&
                      Number(digitsOnly) > 0 &&
                      Number(digitsOnly) <= MAX_UNITS
                    ) {
                      setFreePlanProduct(name);
                    } else {
                      removeFreePlanProduct(name);
                    }

                    const analyticsEvent = createAnalyticsEvent?.({
                      action: 'click',
                      component: 'component',
                      trackEventName: ANALYTICS_EVENTS.userCount,
                      productEdition: selectedPlanName,
                      userCount: Number(digitsOnly),
                      productName: name
                    });

                    analyticsEvent?.fire();
                  }
                }}
                disabled={ccpOfferingSKU === '' && hamsProductKey === ''}
              />
              {name === 'Jira Product Discovery' &&
                selectedPlanName !== 'None' &&
                (selectedPlanName !== 'Free' ? (
                  numUnits > MAX_UNITS ? (
                    <div css={productLimitStyle}>
                      {GetContactMessage(unitName)}
                    </div>
                  ) : (
                    <div
                      css={
                        numUnits !== 0 && numUnits < MIN_UNITS
                          ? productLimitErrorStyle
                          : productLimitStyle
                      }
                    >
                      {GetMinUsersMessage(
                        unitName,
                        localeISOCode,
                        selectedPlanName,
                        MIN_UNITS
                      )}
                    </div>
                  )
                ) : (
                  selectedPlanName === 'Free' && (
                    <div
                      css={
                        numUnits > MAX_UNITS
                          ? productLimitErrorStyle
                          : productLimitStyle
                      }
                    >
                      {GetMaxUsersMessage(
                        unitName,
                        localeISOCode,
                        selectedPlanName,
                        MAX_UNITS
                      )}
                    </div>
                  )
                ))}
              {name === 'Bitbucket' && selectedPlanName === 'Free' && (
                <div
                  css={
                    numUnits > MAX_UNITS
                      ? productLimitErrorStyle
                      : productLimitStyle
                  }
                >
                  {GetMaxUsersMessage(
                    unitName,
                    localeISOCode,
                    selectedPlanName,
                    MAX_UNITS
                  )}
                </div>
              )}
              {name !== 'Bitbucket' &&
                name !== 'Jira Product Discovery' &&
                selectedPlanName !== 'None' &&
                (selectedPlanName !== 'Enterprise' ? (
                  (selectedPlanName !== 'Free' ||
                    selectedBillingCycle !== 'ANNUAL') && (
                    <div
                      css={
                        numUnits > MAX_UNITS
                          ? productLimitErrorStyle
                          : productLimitStyle
                      }
                    >
                      {GetMaxUsersMessage(
                        unitName,
                        localeISOCode,
                        selectedPlanName,
                        MAX_UNITS
                      )}
                    </div>
                  )
                ) : numUnits < MAX_UNITS ? (
                  selectedBillingCycle === 'ANNUAL' && (
                    <div
                      css={
                        numUnits !== 0 && numUnits < MIN_ENTERPRISE_UNITS
                          ? productLimitErrorStyle
                          : productLimitStyle
                      }
                    >
                      {GetEnterpriseMessage(unitName, MIN_ENTERPRISE_UNITS)}
                    </div>
                  )
                ) : (
                  <div css={productLimitStyle}>
                    {GetContactMessage(unitName)}
                  </div>
                ))}
            </div>
          </div>
        </div>
        <div css={pricingContainer}>
          {(ccpOfferingSKU !== '' || hamsProductKey !== '') &&
            numUnits !== 0 &&
            numUnits <= MAX_UNITS &&
            selectedPlanName !== 'Free' &&
            (selectedBillingCycle === 'MONTHLY'
              ? selectedPlanName !== 'Enterprise' && (
                  <div>
                    <div css={monthlyPriceStyle}>
                      {formattedTotal} {monthlyBillingCycleLabel.toLowerCase()}
                    </div>
                    <div css={userPriceStyle}>
                      {perUnit} {GetUnitPriceMessage(unitName)}
                    </div>
                  </div>
                )
              : !noAnnualPlan &&
                (selectedPlanName !== 'Enterprise' ||
                  numUnits >= MIN_ENTERPRISE_UNITS) && (
                  <div>
                    <div css={monthlyPriceStyle}>
                      {formattedTotal} {annuallyBillingCycleLabel.toLowerCase()}
                    </div>
                    <div css={userPriceStyle}>
                      {GetTierMessage(unitName)} {unitLimitLower} -{' '}
                      {unitLimitUpper}
                    </div>
                  </div>
                ))}

          {selectedPlanName === 'Free' &&
            numUnits > 0 &&
            numUnits <= MAX_UNITS && (
              <div>
                <div css={monthlyPriceStyle}>{freeLabel}</div>
              </div>
            )}

          <RemoveButton
            text={removeLabel}
            onClick={() => {
              if (totalSelectedProductNames.size === 1) {
                selectStep(0);
                restart();
              } else {
                incrementTotal(productFamilyName, -currentTotalPrice);
                toggleProductSelection(name);
              }
            }}
            productName={name}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
